import type { ResolvedPathname } from '$app/types';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

/** A navigation offered alongside the message, e.g. to the record at fault. */
export interface ToastLink {
  label: string;
  href: ResolvedPathname;
  /** Open in a new tab, leaving the page the toast is on untouched. */
  newTab?: boolean;
}

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  timestamp: number;
  timeoutMs?: number;
  action?: ToastAction;
  link?: ToastLink;
}

export type ToastType = 'success' | 'error' | 'warning';

class ToastStore {
  private toasts = $state<Toast[]>([]);

  get list() {
    return this.toasts;
  }

  add(
    message: string,
    type: ToastType = 'error',
    timeoutMs?: number,
    action?: ToastAction,
    link?: ToastLink,
  ): string {
    const timestamp = Date.now();
    // Success toasts are transient: auto-dismiss after 3s unless the caller
    // overrides the duration. Without this they linger and stack, and a tall
    // stack can cover (and block clicks on) the UI beneath it. Errors have no
    // default timeout so they persist until the user reads/dismisses them.
    const effectiveTimeoutMs =
      timeoutMs ?? (type === 'success' ? 3000 : undefined);
    const toast: Toast = {
      id: crypto.randomUUID(),
      message,
      type,
      timestamp,
      timeoutMs: effectiveTimeoutMs,
      action,
      link,
    };

    this.toasts.push(toast);

    if (effectiveTimeoutMs !== undefined) {
      setTimeout(() => {
        this.remove(toast.id);
      }, effectiveTimeoutMs);
    }

    return toast.id;
  }

  remove(id: string) {
    const index = this.toasts.findIndex((toast) => toast.id === id);
    if (index > -1) {
      this.toasts.splice(index, 1);
    }
  }

  update(id: string, message: string) {
    const toast = this.toasts.find((t) => t.id === id);
    if (toast) {
      toast.message = message;
    }
  }

  clear() {
    this.toasts = [];
  }
}

export const toastStore = new ToastStore();

// Helper function for easy usage
export function showToast(
  message: string,
  type: ToastType = 'error',
  timeoutMs?: number,
  link?: ToastLink,
) {
  toastStore.add(message, type, timeoutMs, undefined, link);
}
